"use client";

import { useState, useCallback } from "react";
import Script from "next/script";
import { clientEnv } from "@/../env.client";
import { UmbracoEmployee } from "@/types/umbraco";

interface TeamContactInfo {
    leadership: UmbracoEmployee[];
    sales: UmbracoEmployee[];
}

declare global {
    interface Window {
        grecaptcha: {
            ready: (callback: () => void) => void;
            execute: (siteKey: string, options: { action: string }) => Promise<string>;
        };
    }
}

function formatPhoneNumber(phone: string): string {
    // Remove all non-digit characters
    const digits = phone.replace(/\D/g, '');

    // Format based on length
    if (digits.length === 10) {
        // US format: (123) 456-7890
        return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    } else if (digits.length === 11 && digits.startsWith('1')) {
        // US with country code: +1 (123) 456-7890
        return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
    }

    // Return original if format doesn't match expected patterns
    return phone;
};

export function ContactInfoRevealer() {
    const [isVerified, setIsVerified] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [contactInfo, setContactInfo] = useState<TeamContactInfo | null>(null);
    const [scriptLoaded, setScriptLoaded] = useState(false);

    const siteKey = clientEnv.RECAPTCHA_SITE_KEY;

    const handleVerification = useCallback(async () => {
        if (!siteKey) {
            setError("reCAPTCHA is not configured");
            return;
        }

        setIsLoading(true);
        setError(null);

        try {
            // Execute reCAPTCHA v3
            const token = await new Promise<string>((resolve, reject) => {
                window.grecaptcha.ready(() => {
                    window.grecaptcha
                        .execute(siteKey, { action: "view_contact_info" })
                        .then(resolve)
                        .catch(reject);
                });
            });

            // Send token to server for verification
            const response = await fetch("/api/verify-recaptcha", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({ token }),
            });

            const result = await response.json();

            if (!response.ok) {
                throw new Error(result.error || "Verification failed");
            }

            setIsVerified(true);
            setContactInfo(result.contactInfo);
        } catch (err) {
            setError(err instanceof Error ? err.message : "Verification failed");
        } finally {
            setIsLoading(false);
        }
    }, [siteKey]);

    if (!siteKey) {
        return null;
    }

    return (
        <div className="text-center">
            <Script
                src={`https://www.google.com/recaptcha/api.js?render=${siteKey}`}
                onLoad={() => setScriptLoaded(true)}
            />

            {!isVerified ? (
                <button
                    onClick={handleVerification}
                    disabled={isLoading || !scriptLoaded}
                    className="hover:cursor-pointer hover:text-[#C8A21C] transition-colors duration-300 underline disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    {isLoading
                        ? "Verifying..."
                        : scriptLoaded
                            ? "Click here to view contact information"
                            : "Loading..."}
                </button>
            ) : (
                <div className="text-green-600 font-medium">
                    ✓ Contact information revealed below
                </div>
            )}

            {error && <p className="text-red-500 text-sm mt-2">{error}</p>}

            {isVerified && contactInfo && (
                <ContactInfoDisplay contactInfo={contactInfo} />
            )}
        </div>
    );
}

function ContactInfoDisplay({ contactInfo }: { contactInfo: TeamContactInfo }) {
    return (
        <div className="mt-6 p-6 bg-gray-50 rounded-lg shadow-sm w-full max-w-4xl">
            <h3 className="text-xl font-semibold text-gdeepcopper mb-4">
                Contact Information
            </h3>

            {contactInfo.leadership.length > 0 && (
                <div className="mb-6">
                    <h4 className="text-lg font-medium text-gray-700 mb-3">
                        Leadership Team
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {contactInfo.leadership.map((member) => (
                            <ContactCard key={member.fullName} member={member} />
                        ))}
                    </div>
                </div>
            )}

            {contactInfo.sales.length > 0 && (
                <div>
                    <h4 className="text-lg font-medium text-gray-700 mb-3">
                        Sales Team
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {contactInfo.sales.map((member) => (
                            <ContactCard key={member.fullName} member={member} />
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

function ContactCard({ member }: { member: UmbracoEmployee }) {
    return (
        <div className="p-3 bg-white rounded border border-gray-200">
            <p className="font-medium text-gray-800">{member.fullName}</p>
            {member.email && (
                <div className="flex w-full justify-center">
                    <a
                        href={`mailto:${member.email}`}
                        className="text-sm text-blue-600 hover:underline block mt-1 w-fit"
                    >
                        {member.email}
                    </a>
                </div>
            )}
            {member.linkedInUrl && (
                <div className="flex w-full justify-center">
                    <a
                        href={member.linkedInUrl}
                        target="_blank"
                        rel="external nofollow noopener noreferrer"
                        className="text-sm text-blue-600 hover:underline block mt-1 w-fit"
                    >
                        LinkedIn Profile
                    </a>
                </div>
            )}
            {/* {member.phone && (
                <div className="flex w-full justify-center">
                    <a
                        href={`tel:${member.phone}`}
                        target="_blank"
                        rel="external nofollow noopener noreferrer"
                        className="text-sm text-blue-600 hover:underline block mt-1 w-fit"
                    >
                        {formatPhoneNumber(member.phone)}
                    </a>
                </div>
            )} */}
        </div>
    );
}
